import type { GridColumn } from '../types';

// Centralized definition of which pipeline stages count as "done/closed".
// Phase 2 will rename "Done" -> "Closed"; keep that change isolated to this set.
const DONE_LABELS = new Set([
  'done', 'closed', 'complete', 'completed',
  'terminé', 'termine', 'terminée', 'terminee',
  'clôturé', 'cloture', 'clôture', 'cloturé',
  'fermé', 'ferme', 'archivé', 'archive',
]);

/**
 * Returns the set of pipeline status ids that represent a finished project.
 * Matches known "done" labels; if none match, falls back to the terminal stage
 * (highest sort_order) so the feature degrades gracefully on custom pipelines.
 */
export function getDoneStatusIds(pipelineColumns: GridColumn[]): Set<number> {
  const ids = new Set<number>();
  if (!pipelineColumns || pipelineColumns.length === 0) return ids;

  const matched = pipelineColumns.filter(c => DONE_LABELS.has((c.label || '').trim().toLowerCase()));
  if (matched.length > 0) {
    matched.forEach(c => ids.add(c.id));
    return ids;
  }

  // Terminal stage — skipping an archive column, which sits after Done.
  const sorted = [...pipelineColumns]
    .filter(c => !ARCHIVED_LABELS.has((c.label || '').trim().toLowerCase()))
    .sort((a, b) => a.sort_order - b.sort_order);
  if (sorted.length > 0) ids.add(sorted[sorted.length - 1].id);
  return ids;
}

export function isDoneStatus(statusId: number, pipelineColumns: GridColumn[]): boolean {
  return getDoneStatusIds(pipelineColumns).has(statusId);
}

// Mirrors stageColors.ts's BACKLOG_LABELS/DEFERRED_LABELS (kept local there —
// tied to the accent-ramp gradient build) — duplicated here since this check
// doesn't need an accent color, just the label.
const BACKLOG_LABELS = new Set(['backlog']);
const DEFERRED_LABELS = new Set(['deferred', 'différé', 'differe', 'reporté', 'reporte']);

export type ProjectStageGroup = 'active' | 'deferredBacklog' | 'closed';

/** Buckets a project's pipeline stage for views that group by "still moving"
 * vs. parked (Backlog/Deferred) vs. finished (Done/Closed) — e.g. Gérance
 * resources, which lists each group as its own table. */
export function getProjectStageGroup(statusId: number, pipelineColumns: GridColumn[]): ProjectStageGroup {
  if (isDoneStatus(statusId, pipelineColumns)) return 'closed';
  const label = (pipelineColumns.find(c => c.id === statusId)?.label || '').trim().toLowerCase();
  if (BACKLOG_LABELS.has(label) || DEFERRED_LABELS.has(label)) return 'deferredBacklog';
  return 'active';
}

const BLOCKED_LABELS = new Set([
  'blocked', 'bloqué', 'bloque', 'bloquée', 'bloquee',
]);

/**
 * Returns the set of task-board column ids that represent "Blocked" (an
 * admin-named column, e.g. via Admin > Task Board Columns). Unlike
 * done/inbox, there's no fallback — a board without a Blocked column simply
 * has no blocked tasks.
 */
export function getBlockedColumnIds(kanbanColumns: GridColumn[]): Set<number> {
  const ids = new Set<number>();
  if (!kanbanColumns || kanbanColumns.length === 0) return ids;
  kanbanColumns
    .filter(c => BLOCKED_LABELS.has((c.label || '').trim().toLowerCase()))
    .forEach(c => ids.add(c.id));
  return ids;
}

// Mirrors the server's ARCHIVED_LABELS (utils/stages.ts): the task-board
// column for tasks that are no longer relevant — kept for the record, but
// neither open nor done. Plural forms only, so they never overlap DONE_LABELS.
const ARCHIVED_LABELS = new Set([
  'archived', 'archives', 'archivées', 'archivees', 'archivés',
]);

/** Task-board column ids of the "Archivées" column(s). */
export function getArchivedColumnIds(kanbanColumns: GridColumn[]): Set<number> {
  return new Set((kanbanColumns || [])
    .filter(c => ARCHIVED_LABELS.has((c.label || '').trim().toLowerCase()))
    .map(c => c.id));
}

/** Done + archived task-board columns: tasks that need no more work. */
export function getClosedColumnIds(kanbanColumns: GridColumn[]): Set<number> {
  return new Set([...getDoneStatusIds(kanbanColumns), ...getArchivedColumnIds(kanbanColumns)]);
}

const INBOX_LABELS = new Set([
  'inbox', 'intake',
  'boîte de réception', 'boite de reception', 'réception', 'reception',
]);

/**
 * Returns the set of pipeline status ids for the Inbox/intake stage.
 * Falls back to the first stage (lowest sort_order) on custom pipelines.
 */
export function getInboxStatusIds(pipelineColumns: GridColumn[]): Set<number> {
  const ids = new Set<number>();
  if (!pipelineColumns || pipelineColumns.length === 0) return ids;

  const matched = pipelineColumns.filter(c => INBOX_LABELS.has((c.label || '').trim().toLowerCase()));
  if (matched.length > 0) {
    matched.forEach(c => ids.add(c.id));
    return ids;
  }

  const sorted = [...pipelineColumns].sort((a, b) => a.sort_order - b.sort_order);
  ids.add(sorted[0].id);
  return ids;
}

// ── Stage tabs (progressive disclosure of the project lifecycle form) ──

export type StageTab = 'overview' | 'prequalification' | 'scoping' | 'qualification' | 'planning' | 'execution' | 'handover';

export const STAGE_TAB_LABELS: Record<StageTab, string> = {
  overview: 'Overview',
  prequalification: 'Préqualification',
  scoping: 'Scoping',
  qualification: 'Qualification',
  planning: 'Planning',
  execution: 'Execution',
  handover: 'Handover',
};

const TAB_ORDER: StageTab[] = ['overview', 'prequalification', 'scoping', 'qualification', 'planning', 'execution', 'handover'];

// Map a pipeline stage label to the furthest tab it unlocks.
export const STAGE_LABEL_TO_TAB: Record<string, StageTab> = {
  'Inbox': 'overview',
  'Préqualification': 'prequalification',
  'Scoping': 'scoping',
  'Qualification': 'qualification',
  'Planning': 'planning',
  'Execution': 'execution',
  'Handover': 'handover',
  'Closed': 'handover',
};

// Where a pipeline stage label sits relative to Execution — used to decide
// whether a project's PMO-assessed RAG health applies yet ('execution'), is
// not reached yet ('before', shown as a dash), or is done ('after', a checkmark).
export function getExecutionProgress(label: string): 'before' | 'execution' | 'after' {
  const tab = label === 'Deferred' ? 'prequalification' : (STAGE_LABEL_TO_TAB[label] ?? 'overview');
  const idx = TAB_ORDER.indexOf(tab);
  const execIdx = TAB_ORDER.indexOf('execution');
  if (idx < execIdx) return 'before';
  if (idx === execIdx) return 'execution';
  return 'after';
}

// Whether a project's current pipeline stage is Planning or Execution — the
// window during which its milestones are actively defined/worked, used to
// scope the Gérance meta/calendar views to projects worth showing there.
export function isPlanningOrExecutionStage(statusId: number, pipelineColumns: GridColumn[]): boolean {
  const label = pipelineColumns.find(c => c.id === statusId)?.label || '';
  const tab = STAGE_LABEL_TO_TAB[label];
  return tab === 'planning' || tab === 'execution';
}

// Progressive tab disclosure: show every tab up to the project's current stage.
export function getAvailableTabs(statusId: number, pipelineColumns: GridColumn[]): { tabs: StageTab[]; defaultTab: StageTab } {
  const col = pipelineColumns.find(c => c.id === statusId);
  const label = col?.label || 'Inbox';
  if (label === 'Deferred') return { tabs: ['overview', 'prequalification'], defaultTab: 'prequalification' };
  const unlocked = STAGE_LABEL_TO_TAB[label];
  if (!unlocked || unlocked === 'overview') return { tabs: ['overview'], defaultTab: 'overview' };
  const idx = TAB_ORDER.indexOf(unlocked);
  return { tabs: TAB_ORDER.slice(0, idx + 1), defaultTab: unlocked };
}

// ── Project page: one stage bar for the process, topic tabs for the content ──
//
// The project page used to have one tab per pipeline stage, each holding the
// fields entered at that stage — so a topic (team, time, budget, deliverables)
// ended up spread over several tabs. The page now separates the two: a stage
// bar shows where the project is and what the next stage needs, and the
// content is grouped by topic, each field shown once and unlocked by the stage
// at which it is filled.

/** The linear lifecycle, in order (side-states Deferred/Backlog excluded). */
export const LIFECYCLE_STAGES = ['Inbox', 'Préqualification', 'Scoping', 'Qualification', 'Planning', 'Execution', 'Handover', 'Closed'] as const;
export type LifecycleStage = typeof LIFECYCLE_STAGES[number];

export const RANK = {
  inbox: 0, prequalification: 1, scoping: 2, qualification: 3, planning: 4, execution: 5, handover: 6, closed: 7,
} as const;

/**
 * How far a project has come in the lifecycle (index in LIFECYCLE_STAGES).
 * Side-states count as the point they branched off: Deferred is decided at
 * Préqualification; Backlog follows the council's Qualification when the
 * project was qualified, else it is parked before anything was scoped.
 */
export function lifecycleRank(label: string, qualifiedAt?: string | null): number {
  const i = (LIFECYCLE_STAGES as readonly string[]).indexOf(label);
  if (i >= 0) return i;
  if (label === 'Deferred') return RANK.prequalification;
  if (label === 'Backlog') return qualifiedAt ? RANK.qualification : RANK.inbox;
  return RANK.inbox;
}

// 'etape' is the work of one stage ("À faire"): the fields that stage asks
// for, as numbered steps, ending with its decision. It is not one of the
// fixed topic tabs: the page shows it first while a stage has work to do.
export type ProjectTopic = 'etape' | 'apercu' | 'contenu' | 'equipe' | 'strategie' | 'plan' | 'cloture';

// Plan et exécution comes right after the Aperçu: it is the tab a project team
// works in day to day once the project runs.
export const PROJECT_TOPICS: ProjectTopic[] = ['apercu', 'plan', 'contenu', 'equipe', 'strategie', 'cloture'];

/** Stage from which a topic has something to fill (before it, the tab explains what comes). */
export const TOPIC_UNLOCK_RANK: Record<ProjectTopic, number> = {
  etape: RANK.inbox,
  apercu: RANK.inbox,
  contenu: RANK.inbox,
  equipe: RANK.inbox,
  strategie: RANK.prequalification,
  plan: RANK.planning,
  cloture: RANK.handover,
};

/** Old stage-tab deep links (#project?id=…&tab=execution) → the topic that now holds that content. */
export const LEGACY_TAB_TO_TOPIC: Record<StageTab, ProjectTopic> = {
  overview: 'apercu',
  prequalification: 'strategie',
  scoping: 'equipe',
  qualification: 'strategie',
  planning: 'plan',
  execution: 'plan',
  handover: 'cloture',
};

/** A `tab=` URL value → topic: a topic name, an old stage-tab name, or null. */
export function topicFromParam(value: string | null): ProjectTopic | null {
  if (!value) return null;
  if (value === 'etape' || (PROJECT_TOPICS as string[]).includes(value)) return value as ProjectTopic;
  if (Object.prototype.hasOwnProperty.call(LEGACY_TAB_TO_TOPIC, value)) return LEGACY_TAB_TO_TOPIC[value as StageTab];
  return null;
}

/** Stages with an "À faire" view (Execution works in Plan et exécution, Closed in Clôture). */
export const STAGES_WITH_VIEW = ['Inbox', 'Préqualification', 'Scoping', 'Qualification', 'Planning', 'Handover'] as const;
